import React, { useState, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const FileUploadZone = ({ onFilesSelected, isProcessing = false }) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const handleDragOver = (e) => {
    e?.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e?.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    setIsDragOver(false);
    
    const files = Array.from(e?.dataTransfer?.files);
    const validFiles = files?.filter(file => 
      file?.type === 'text/csv' || 
      file?.type === 'application/vnd.ms-excel' ||
      file?.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    );
    
    if (validFiles?.length > 0) {
      onFilesSelected(validFiles);
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e?.target?.files);
    if (files?.length > 0) {
      onFilesSelected(files);
    }
    // Reset input value to allow selecting the same file again
    e.target.value = '';
  };

  const handleBrowseClick = () => {
    fileInputRef?.current?.click();
  };

  return (
    <div className="w-full">
      <div
        className={`
          relative border-2 border-dashed rounded-lg p-8 text-center transition-all duration-200
          ${isDragOver 
            ? 'border-primary bg-primary bg-opacity-5 scale-[1.02]' 
            : 'border-border bg-card hover:border-primary hover:bg-muted'
          }
          ${isProcessing ? 'opacity-50 pointer-events-none' : 'cursor-pointer'}
        `}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleBrowseClick}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept=".csv,.xlsx,.xls"
          onChange={handleFileSelect}
          className="hidden"
          disabled={isProcessing}
        />

        <div className="flex flex-col items-center space-y-4">
          {/* Upload Icon */}
          <div className={`
            w-16 h-16 rounded-full flex items-center justify-center transition-colors duration-200
            ${isDragOver ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}
          `}>
            <Icon 
              name={isDragOver ? "Upload" : "FileSpreadsheet"} 
              size={32} 
              className={isDragOver ? "animate-bounce" : ""}
            />
          </div>

          {/* Main Message */}
          <div className="space-y-2">
            <h3 className="text-lg font-semibold text-foreground">
              {isDragOver ? "Drop files here" : "Upload Supplier Price Lists"}
            </h3>
            <p className="text-sm text-muted-foreground max-w-md">
              Drag and drop your CSV or Excel files here, or click to browse. 
              You can upload multiple files at once for batch processing.
            </p>
          </div>

          {/* File Format Indicators */}
          <div className="flex items-center space-x-4 text-xs text-muted-foreground">
            <div className="flex items-center space-x-1">
              <Icon name="FileText" size={14} />
              <span>CSV</span>
            </div>
            <div className="flex items-center space-x-1">
              <Icon name="FileSpreadsheet" size={14} />
              <span>XLSX</span>
            </div>
            <div className="flex items-center space-x-1">
              <Icon name="FileSpreadsheet" size={14} />
              <span>XLS</span>
            </div>
          </div>

          {/* Browse Button */}
          <Button
            variant="outline"
            size="lg"
            iconName="FolderOpen"
            iconPosition="left"
            disabled={isProcessing}
            className="mt-4"
          >
            Browse Files
          </Button>
        </div>

        {/* Processing Overlay */}
        {isProcessing && (
          <div className="absolute inset-0 bg-background bg-opacity-50 flex items-center justify-center rounded-lg">
            <div className="flex items-center space-x-2 text-muted-foreground">
              <Icon name="Loader2" size={20} className="animate-spin" />
              <span className="text-sm">Processing files...</span>
            </div>
          </div>
        )}
      </div>

      {/* Upload Guidelines */}
      <div className="mt-4 p-4 bg-muted rounded-lg">
        <h4 className="text-sm font-medium text-foreground mb-2">Upload Guidelines:</h4>
        <ul className="text-xs text-muted-foreground space-y-1">
          <li>• Maximum file size: 50MB per file</li>
          <li>• Supported formats: CSV, XLSX, XLS</li>
          <li>• Files should contain product data with columns like Name, Brand, Price, SKU</li>
          <li>• Multiple supplier files can be uploaded simultaneously</li>
        </ul>
      </div>
    </div>
  );
};

export default FileUploadZone;